import { graphqlQuery } from '@/lib/api/graphql';
import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { useModeAwareQuery } from './useModeAwareQuery';
import { RECEIPT_TOTALS_QUERY, type ReceiptTotalsGraphQLResponse } from './useReceiptsGraphQL';

export interface UnallocatedPaymentsSummaryApi {
  unallocatedCount: number;
  totalOutstandingAmount: number;
}

/**
 * Hook to fetch unallocated payments summary.
 * In live mode, calls the GraphQL receiptTotals query.
 * In demo mode, returns data derived from the global data store.
 * @param creditControllerId — when provided (live mode only), filters receipts
 *   scoped to customers assigned to that credit controller. Pass `undefined` for all.
 */
export function useUnallocatedPaymentsSummary(creditControllerId?: string) {
  const apiConfig = useApiConfig();

  const store = getGlobalDataStore();
  const unallocatedReceipts = store.getUnallocatedReceipts();
  const demoData: UnallocatedPaymentsSummaryApi = {
    unallocatedCount: unallocatedReceipts.length,
    totalOutstandingAmount: unallocatedReceipts.reduce((sum, r) => sum + r.unappliedAmount, 0),
  };

  return useModeAwareQuery<UnallocatedPaymentsSummaryApi, Error>(
    ['unallocated-payments-summary', creditControllerId ?? 'everyone'],
    async ({ signal }) => {
      const filter: Record<string, unknown> = {};
      if (creditControllerId) {
        filter.creditControllerId = { eq: Number(creditControllerId) };
      }

      const response = await graphqlQuery<ReceiptTotalsGraphQLResponse>(
        apiConfig,
        RECEIPT_TOTALS_QUERY,
        { filter },
        signal,
      );

      const totals = response.receipts.receiptTotals;

      return {
        unallocatedCount: totals.unallocatedCount,
        totalOutstandingAmount: totals.unallocatedAmount,
      };
    },
    demoData,
    { staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: false },
  );
}
